import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { SystemChangeHistoryState } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';

const searchCommon = async (
    { dispatch, commit },
    { searchItems, pageNumber, perPage, sort },
    url,
    { start, success, failure }
) => {
    const payload = {
        start_date: searchItems.startDate,
        end_date: searchItems.endDate,
        event: searchItems.event,
        page_number: pageNumber,
        per_page: perPage,
        'sort[column]': sort.column,
        'sort[direction]': sort.direction,
    };
    try {
        commit(start);
        const resData = await dispatch('auth/api', { method: 'get', url: url, params: payload }, { root: true });
        if (isHappenedOtherPartyError(resData)) {
            // システムエラー・予期せぬエラー時
            commit(types.FATAL_FAILURE);
        } else {
            // 成功時
            commit(success, resData);
        }
    } catch (resData) {
        // アプリケーションエラー時
        commit(failure, resData.errors[0]);
    }
};

const csvCommon = async ({ dispatch, commit, rootState }, { searchItems, sort }, url) => {
    const payload = {
        start_date: searchItems.startDate,
        end_date: searchItems.endDate,
        event: searchItems.event,
        sort: sort,
    };
    try {
        commit(types.CSV_OUTPUT_START);
        const resData = await dispatch('auth/api', { method: 'post', url: url, params: payload }, { root: true });
        if (isHappenedOtherPartyError(resData)) {
            // システムエラー・予期せぬエラー時
            commit(types.FATAL_FAILURE);
        } else {
            // 成功時
            commit(types.CSV_OUTPUT_SUCCESS);
            // 成功トースト表示
            const i18n = (rootState as any).i18n;
            const message = translator(i18n, MessageKey.Csv.OUTPUT);
            dispatch('toastMessage', message, { root: true });
        }
    } catch (resData) {
        // アプリケーションエラー時
        commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
    }
};

export const actions: ActionTree<SystemChangeHistoryState, RootState> = {
    /**
     * システム変更履歴検索(ユーザー)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async user(context: { dispatch; commit }, payload: { searchItems; pageNumber; perPage; sort }) {
        await searchCommon(context, payload, ApiUrl.SystemChangeHistory.USER, {
            start: types.USER_START,
            success: types.USER_SUCCESS,
            failure: types.USER_FAILURE,
        });
    },

    /**
     * システム変更履歴検索(メール)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async mail(context: { dispatch; commit }, payload: { searchItems; pageNumber; perPage; sort }) {
        await searchCommon(context, payload, ApiUrl.SystemChangeHistory.MAIL, {
            start: types.MAIL_START,
            success: types.MAIL_SUCCESS,
            failure: types.MAIL_FAILURE,
        });
    },

    /**
     * システム変更履歴検索(ノード)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async node(context: { dispatch; commit }, payload: { searchItems; pageNumber; perPage; sort }) {
        await searchCommon(context, payload, ApiUrl.SystemChangeHistory.NODE, {
            start: types.NODE_START,
            success: types.NODE_SUCCESS,
            failure: types.NODE_FAILURE,
        });
    },

    /**
     * システム変更履歴検索(システム設定管理)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async systemSetting(context: { dispatch; commit }, payload: { searchItems; pageNumber; perPage; sort }) {
        await searchCommon(context, payload, ApiUrl.SystemChangeHistory.SYSTEM_SETTING, {
            start: types.SYSTEM_SETTING_START,
            success: types.SYSTEM_SETTING_SUCCESS,
            failure: types.SYSTEM_SETTING_FAILURE,
        });
    },

    /**
     * システム変更履歴検索(アカウント棚卸設定管理)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async accountInventorySetting(context: { dispatch; commit }, payload: { searchItems; pageNumber; perPage; sort }) {
        await searchCommon(context, payload, ApiUrl.SystemChangeHistory.ACCOUNT_INVENTORY_SETTING, {
            start: types.ACCOUNT_INVENTORY_START,
            success: types.ACCOUNT_INVENTORY_SUCCESS,
            failure: types.ACCOUNT_INVENTORY_FAILURE,
        });
    },

    /**
     * システム変更履歴CSV出力(ユーザー)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索条件
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async userOutput(context: { dispatch; commit; rootState }, payload: { searchItems; sort }) {
        await csvCommon(context, payload, ApiUrl.SystemChangeHistory.USER_OUTPUT);
    },

    /**
     * システム変更履歴CSV出力(メール)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索条件
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async mailOutput(context: { dispatch; commit; rootState }, payload: { searchItems; sort }) {
        await csvCommon(context, payload, ApiUrl.SystemChangeHistory.MAIL_OUTPUT);
    },

    /**
     * システム変更履歴CSV出力(ノード)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索条件
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async nodeOutput(context: { dispatch; commit; rootState }, payload: { searchItems; sort }) {
        await csvCommon(context, payload, ApiUrl.SystemChangeHistory.NODE_OUTPUT);
    },

    /**
     * システム変更履歴CSV出力(システム設定管理)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索条件
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async systemSettingOutput(context: { dispatch; commit; rootState }, payload: { searchItems; sort }) {
        await csvCommon(context, payload, ApiUrl.SystemChangeHistory.SYSTEM_SETTING_OUTPUT);
    },

    /**
     * システム変更履歴CSV出力(アカウント棚卸設定管理)
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索条件
     * @param {SortParam} sort ソートキーとソート順
     * @returns {Promise<void>}
     */
    async accountInventorySettingOutput(context: { dispatch; commit; rootState }, payload: { searchItems; sort }) {
        await csvCommon(context, payload, ApiUrl.SystemChangeHistory.ACCOUNT_INVENTORY_SETTING_OUTPUT);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
